import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const TermsPage: React.FC = () => {
  const navigate = useNavigate();

  const sectionStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const headingStyle: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontSize: '20px',
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '16px',
  };

  const paragraphStyle: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontSize: '15px',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.8,
    marginBottom: '12px',
  };

  const listStyle: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontSize: '15px',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.8,
    marginLeft: '24px',
    marginBottom: '12px',
  };

  return (
    <div
      style={{
        backgroundColor: '#050505',
        minHeight: '100vh',
        color: 'white',
        padding: '80px 40px',
      }}
    >
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <button
          onClick={() => navigate('/')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.6)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            cursor: 'pointer',
            marginBottom: '40px',
            padding: 0,
          }}
        >
          <ArrowLeft size={16} />
          Back to home
        </button>

        <h1
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '42px',
            fontWeight: 600,
            marginBottom: '16px',
          }}
        >
          Terms of Service
        </h1>

        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
            color: 'rgba(255,255,255,0.5)',
            marginBottom: '40px',
          }}
        >
          Last Updated: January 2026
        </p>

        <p style={paragraphStyle}>
          These Terms of Service ("Terms") govern your access to and use of Jamie, PullThatUpJamie, and related services, websites, applications, APIs, and tools (collectively, the "Service") operated by CASCDR Inc. ("Company," "we," "us," or "our").
        </p>

        <p style={{ ...paragraphStyle, fontWeight: 500, color: 'rgba(255,255,255,0.8)' }}>
          By accessing or using the Service, you agree to be bound by these Terms.
        </p>

        {/* Section 1 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Eligibility & Acceptance</h2>
          <p style={paragraphStyle}>
            You must be at least 13 years old (or the minimum legal age in your jurisdiction) to use the Service. By using the Service, you represent that you meet this requirement and have the authority to enter into these Terms.
          </p>
          <p style={{ ...paragraphStyle, fontWeight: 500 }}>
            If you do not agree, do not use the Service.
          </p>
        </div>

        {/* Section 2 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Description of the Service</h2>
          <p style={paragraphStyle}>The Service provides tools for:</p>
          <ul style={listStyle}>
            <li>search, indexing, summarization, transcription, and analysis of audio, video, and text content;</li>
            <li>AI-assisted discovery, embeddings, and content navigation;</li>
            <li>user-submitted queries, links, files, or other inputs.</li>
          </ul>
          <p style={paragraphStyle}>
            The Service is provided for informational and exploratory purposes only. We do not guarantee accuracy, completeness, legality, or suitability of any output.
          </p>
        </div>

        {/* Section 3 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. No Professional Advice</h2>
          <p style={paragraphStyle}>
            The Service does not provide legal, medical, financial, or other professional advice.
          </p>
          <p style={paragraphStyle}>Any information or output generated by the Service:</p>
          <ul style={listStyle}>
            <li>is not a substitute for professional advice;</li>
            <li>should not be relied upon as fact;</li>
            <li>may be incomplete, outdated, incorrect, or misleading.</li>
          </ul>
          <p style={{ ...paragraphStyle, fontWeight: 500 }}>
            You assume full responsibility for how you use the Service and its outputs.
          </p>
        </div>

        {/* Section 4 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. User Accounts & Access</h2>
          <p style={paragraphStyle}>
            We may require you to create an account or authenticate to access some or all of the Service.
          </p>
          <p style={paragraphStyle}>You are responsible for:</p>
          <ul style={listStyle}>
            <li>maintaining the confidentiality of your credentials;</li>
            <li>all activity that occurs under your account.</li>
          </ul>
          <p style={paragraphStyle}>
            We are not liable for unauthorized access caused by your failure to safeguard credentials.
          </p>
        </div>

        {/* Section 5 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. User Content</h2>
          
          <h3 style={{ ...headingStyle, fontSize: '17px', marginTop: '20px' }}>5.1 Your Responsibility</h3>
          <p style={paragraphStyle}>
            You are solely responsible for any content you submit, upload, link, query, or process through the Service ("User Content").
          </p>
          <p style={paragraphStyle}>You represent and warrant that:</p>
          <ul style={listStyle}>
            <li>you have all necessary rights to submit the User Content;</li>
            <li>the User Content does not violate any law or third-party rights.</li>
          </ul>

          <h3 style={{ ...headingStyle, fontSize: '17px', marginTop: '20px' }}>5.2 License to Us</h3>
          <p style={paragraphStyle}>
            By submitting User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, process, store, reproduce, and display such content solely for the purpose of operating, improving, and providing the Service.
          </p>
        </div>

        {/* Section 6 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Prohibited Uses</h2>
          <p style={paragraphStyle}>You agree not to use the Service to:</p>
          <ul style={listStyle}>
            <li>violate any law or regulation;</li>
            <li>infringe intellectual property or privacy rights;</li>
            <li>harass, threaten, or abuse others;</li>
            <li>attempt to bypass safeguards, rate limits, or access controls;</li>
            <li>scrape, reverse engineer, or exploit the Service except as expressly permitted;</li>
            <li>use the Service in a manner that poses legal, security, or reputational risk to us or others.</li>
          </ul>
        </div>

        {/* Section 7 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Suspension & Termination (Very Important)</h2>
          <p style={paragraphStyle}>We reserve the absolute right, at our sole discretion, to:</p>
          <ul style={listStyle}>
            <li>suspend, restrict, or terminate your access to the Service;</li>
            <li>remove or limit content;</li>
            <li>deny future access;</li>
          </ul>
          <p style={paragraphStyle}>with or without notice, and for any reason, including but not limited to:</p>
          <ul style={listStyle}>
            <li>suspected abuse or misuse;</li>
            <li>legal or regulatory concerns;</li>
            <li>security risks;</li>
            <li>operational, reputational, or business reasons.</li>
          </ul>
          <p style={{ ...paragraphStyle, fontWeight: 500 }}>
            We are not obligated to provide an explanation.
          </p>
        </div>

        {/* Section 8 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Availability & Changes</h2>
          <p style={paragraphStyle}>
            The Service is provided on an "AS IS" and "AS AVAILABLE" basis.
          </p>
          <p style={paragraphStyle}>We may:</p>
          <ul style={listStyle}>
            <li>modify, suspend, or discontinue any part of the Service at any time;</li>
            <li>change these Terms at any time by posting updated versions.</li>
          </ul>
          <p style={paragraphStyle}>
            Continued use constitutes acceptance of changes.
          </p>
        </div>

        {/* Section 9 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Third-Party Services & Content</h2>
          <p style={paragraphStyle}>
            The Service may integrate or link to third-party services, APIs, models, or content.
          </p>
          <p style={paragraphStyle}>
            We do not control and are not responsible for third-party services, their availability, accuracy, or legality. Use them at your own risk.
          </p>
        </div>

        {/* Section 10 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Disclaimers</h2>
          <p style={paragraphStyle}>To the maximum extent permitted by law:</p>
          <ul style={listStyle}>
            <li>we make no warranties, express or implied;</li>
            <li>we disclaim all warranties of merchantability, fitness for a particular purpose, and non-infringement;</li>
            <li>we do not guarantee uninterrupted, secure, or error-free operation.</li>
          </ul>
        </div>

        {/* Section 11 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Limitation of Liability</h2>
          <p style={paragraphStyle}>To the fullest extent permitted by law, CASCDR Inc. shall not be liable for:</p>
          <ul style={listStyle}>
            <li>indirect, incidental, special, consequential, or punitive damages;</li>
            <li>loss of data, profits, goodwill, or business opportunities;</li>
            <li>reliance on Service outputs.</li>
          </ul>
          <p style={paragraphStyle}>
            Our total liability for any claim shall not exceed the amount you paid us (if any) in the prior 12 months.
          </p>
        </div>

        {/* Section 12 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Indemnification</h2>
          <p style={paragraphStyle}>
            You agree to indemnify and hold harmless CASCDR Inc., its officers, directors, employees, and contractors from any claims, damages, losses, or expenses arising from:
          </p>
          <ul style={listStyle}>
            <li>your use of the Service;</li>
            <li>your User Content;</li>
            <li>your violation of these Terms or applicable law.</li>
          </ul>
        </div>

        {/* Section 13 */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>13. Governing Law & Venue</h2>
          <p style={paragraphStyle}>
            These Terms are governed by the laws of the State of Delaware, without regard to conflict-of-law principles.
          </p>
          <p style={paragraphStyle}>
            Any dispute shall be brought exclusively in state or federal courts located in Delaware.
          </p>
        </div>

        {/* Footer */}
        <div
          style={{
            marginTop: '60px',
            paddingTop: '24px',
            borderTop: '1px solid rgba(255,255,255,0.1)',
          }}
        >
          <p
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.4)',
            }}
          >
            © {new Date().getFullYear()} CASCDR Inc. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
